/**
 * PeriodExportCard — Zeitraum-Export (von–bis) über alle Kunden.
 *
 * Erstellt im Hintergrund ein wiederherstellbares ZIP (gleiches Format wie
 * das Backup → kann in der Preview via "Wiederherstellen" eingespielt werden),
 * gefiltert auf einen Datumsbereich und einzeln wählbare Datenkategorien.
 * Ergebnis: Download-Button + optional E-Mail an den Admin.
 */
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Separator } from '../ui/separator';
import { CalendarRange, Download, Mail, RefreshCcw, FileArchive } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

const PeriodExportCard = () => {
  const [categories, setCategories] = useState([]);
  const [selected, setSelected] = useState({});
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [includeRelated, setIncludeRelated] = useState(true);
  const [allCustomers, setAllCustomers] = useState(false);
  const [sendEmail, setSendEmail] = useState(false);
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);

  const loadCategories = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/admin/period-export/categories`, auth());
      setCategories(r.data.categories || []);
      const init = {};
      (r.data.categories || []).forEach((c) => { init[c.key] = false; });
      setSelected(init);
    } catch {
      // st: harmlos, Karte bleibt nutzbar sobald Backend wieder da ist
    }
  }, []);

  useEffect(() => { loadCategories(); }, [loadCategories]);

  const allSelected = categories.length > 0 && categories.every((c) => selected[c.key]);
  const toggleAll = (v) => {
    const next = {};
    categories.forEach((c) => { next[c.key] = v; });
    setSelected(next);
  };

  const downloadZip = async (jobId, filename) => {
    try {
      const resp = await axios.get(`${API}/admin/period-export/download/${jobId}`, {
        ...auth(), responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([resp.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = filename || 'export.zip';
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error('Download fehlgeschlagen');
    }
  };

  const pollJob = async (jobId) => {
    for (let i = 0; i < 240; i++) {
      await new Promise((r) => setTimeout(r, 3000));
      try {
        const s = await axios.get(`${API}/admin/period-export/job/${jobId}`, auth());
        const job = s.data;
        if (job.status === 'done') return job;
        if (job.status === 'error') { throw new Error(job.error || 'Unbekannter Fehler'); }
      } catch (e) {
        if (e.message && !e.response) throw e; // echter Fehler
        // Netzwerk/504 → weiter pollen
      }
    }
    throw new Error('Timeout — Export dauert zu lange');
  };

  const startExport = async () => {
    const chosen = categories.filter((c) => selected[c.key]).map((c) => c.key);
    if (!fromDate || !toDate) { toast.error('Bitte Zeitraum (von / bis) wählen'); return; }
    if (chosen.length === 0) { toast.error('Bitte mindestens eine Datenkategorie wählen'); return; }
    setRunning(true);
    setResult(null);
    try {
      const r = await axios.post(`${API}/admin/period-export`, {
        from_date: fromDate,
        to_date: toDate,
        categories: chosen,
        include_related: includeRelated,
        all_customers: allCustomers,
        send_email: sendEmail,
      }, auth());
      toast.info('Export läuft im Hintergrund…');
      const job = await pollJob(r.data.job_id);
      setResult(job);
      toast.success(`Export fertig: ${job.total_docs} Datensätze (${job.size_mb} MB)`);
      if (job.email_sent) toast.success(`Per E-Mail gesendet an ${job.recipient}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || 'Export fehlgeschlagen');
    } finally {
      setRunning(false);
    }
  };

  return (
    <Card data-testid="period-export-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <CalendarRange className="h-5 w-5 text-indigo-600" />
          Zeitraum-Export (von–bis)
        </CardTitle>
        <CardDescription>
          Lädt ausgewählte Daten eines Zeitraums über <strong>alle Kunden</strong> als
          wiederherstellbares ZIP (kann in der Preview via „Wiederherstellen" eingespielt werden).
          Läuft im Hintergrund.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Zeitraum */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="export-from">Von</Label>
            <input
              id="export-from" type="date" value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              className="mt-1 flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              data-testid="period-export-from"
            />
          </div>
          <div>
            <Label htmlFor="export-to">Bis</Label>
            <input
              id="export-to" type="date" value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              className="mt-1 flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              data-testid="period-export-to"
            />
          </div>
        </div>

        <Separator />

        {/* Kategorien */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <Label className="font-semibold">Welche Daten exportieren?</Label>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input
                type="checkbox" checked={allSelected}
                onChange={(e) => toggleAll(e.target.checked)}
                className="rounded" data-testid="period-export-all"
              />
              Alles auswählen
            </label>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {categories.map((c) => (
              <label key={c.key} className="flex items-center gap-2 text-sm cursor-pointer p-2 rounded-md border hover:bg-muted/50">
                <input
                  type="checkbox"
                  checked={!!selected[c.key]}
                  onChange={(e) => setSelected((s) => ({ ...s, [c.key]: e.target.checked }))}
                  className="rounded"
                  data-testid={`period-export-cat-${c.key}`}
                />
                {c.label}
              </label>
            ))}
          </div>
        </div>

        <Separator />

        {/* Optionen */}
        <div className="flex items-center justify-between">
          <Label className="flex flex-col">
            <span className="font-medium">Alle Kunden & Nutzer exportieren</span>
            <span className="text-xs text-muted-foreground">Exportiert ALLE Kundenprofile 1:1 (nicht nur die im Zeitraum aktiven) — z.B. um doppelte Konten in der Preview nachzustellen</span>
          </Label>
          <Switch checked={allCustomers} onCheckedChange={setAllCustomers} data-testid="period-export-all-customers" />
        </div>
        <div className="flex items-center justify-between">
          <Label className="flex flex-col">
            <span className="font-medium">Verknüpfte Kunden & Nutzer mitexportieren</span>
            <span className="text-xs text-muted-foreground">{allCustomers ? 'Deaktiviert — es werden bereits alle Kunden exportiert' : 'Empfohlen — macht die Wiederherstellung in der Preview vollständig'}</span>
          </Label>
          <Switch checked={includeRelated} onCheckedChange={setIncludeRelated} disabled={allCustomers} data-testid="period-export-related" />
        </div>
        <div className="flex items-center justify-between">
          <Label className="flex items-center gap-2 font-medium">
            <Mail className="h-4 w-4" /> Zusätzlich per E-Mail an Admin senden
          </Label>
          <Switch checked={sendEmail} onCheckedChange={setSendEmail} data-testid="period-export-email" />
        </div>

        <Button onClick={startExport} disabled={running} className="bg-indigo-600 hover:bg-indigo-700" data-testid="period-export-start">
          {running ? <><RefreshCcw className="h-4 w-4 mr-2 animate-spin" /> Export läuft…</> : <><Download className="h-4 w-4 mr-2" /> Export erstellen</>}
        </Button>

        {/* Ergebnis */}
        {result && result.status === 'done' && (
          <div className="p-4 rounded-lg border bg-green-50 border-green-200 space-y-2" data-testid="period-export-result">
            <div className="flex items-center gap-2 font-semibold text-green-900">
              <FileArchive className="h-5 w-5 text-green-600" />
              Export fertig — {result.total_docs} Datensätze ({result.size_mb} MB)
            </div>
            <ul className="text-sm text-green-800 space-y-0.5">
              {(result.stats || []).map((s) => (
                <li key={s.category}>• {s.label}: {s.count}</li>
              ))}
            </ul>
            {result.email_sent && (
              <p className="text-xs text-green-700">✉️ Per E-Mail gesendet an {result.recipient}</p>
            )}
            <Button size="sm" variant="outline" onClick={() => downloadZip(result.id, result.filename)} data-testid="period-export-download">
              <Download className="h-4 w-4 mr-1" /> ZIP herunterladen
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default PeriodExportCard;
